import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ShieldCheck, Clock } from "lucide-react";
import { RevealLines } from "./RevealText";
import { BookingForm } from "./BookingForm";

const HERO_IMG =
  "https://images.pexels.com/photos/35644782/pexels-photo-35644782.jpeg?auto=compress&cs=tinysrgb&w=1600&q=70";

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-jodhpur-primary grain-overlay"
    >
      {/* Parallax background image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="The Blue City of Jodhpur"
          width="1600"
          height="900"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover"
        />
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-b from-jodhpur-primary/70 via-jodhpur-primary/60 to-jodhpur-ink"
        />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-40 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          {/* Left: kinetic copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-4 py-1.5 mb-7"
            >
              <span className="flex items-center gap-1 text-jodhpur-accent">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-current" />
                ))}
              </span>
              <span className="text-white/90 text-xs sm:text-sm font-medium">
                Trusted by 12,000+ travellers across Rajasthan
              </span>
            </motion.div>

            <h1
              data-testid="hero-heading"
              className="font-display text-white leading-[0.95] tracking-[-0.02em] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem]"
              style={{ perspective: 800 }}
            >
              <RevealLines
                lines={["Ride the", "Blue City &", "beyond."]}
                lineClassName="italic"
                delay={0.15}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-6 max-w-xl text-white/80 text-base sm:text-lg leading-relaxed"
            >
              Local & outstation taxi service in Jodhpur. Clean cars, expert
              drivers and honest fares — from a quick city hop to Jaipur,
              Udaipur, Jaisalmer & Nakoda Ji.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="mt-8 flex flex-wrap gap-x-8 gap-y-3"
            >
              {[
                { icon: ShieldCheck, label: "Safe & sanitised fleet" },
                { icon: Clock, label: "24×7 availability" },
                { icon: Star, label: "Fixed, no-surge pricing" },
              ].map((f) => (
                <div key={f.label} className="flex items-center gap-2 text-white/85 text-sm">
                  <f.icon className="h-4 w-4 text-jodhpur-accent" />
                  {f.label}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: booking form */}
          <div className="lg:col-span-5">
            <BookingForm />
          </div>
        </div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2"
      >
        <span className="text-white/60 text-[11px] uppercase tracking-[0.3em]">Scroll</span>
        <div className="h-9 w-5 rounded-full border border-white/40 flex justify-center pt-1.5">
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.6 }}
            className="h-1.5 w-1 rounded-full bg-white/70"
          />
        </div>
      </motion.div>
    </section>
  );
};
